import React from 'react';
import { useTranslation } from 'react-i18next';
import { FinalCompetitionConfiguration, FinalCompetitionPhase, LaneData } from '../models/Models';
import FinalCompetitionTarget from './FinalCompetitionTarget';
import WeatherLowerThird from './WeatherLowerThird';
import ShootOffBanner, { ShootOffView } from './ShootOffBanner';
import ShootOffSpotlight from './ShootOffSpotlight';
import { Translation } from '../helper/WinfireLiveConstHelper';
import './FinalCompetition.css';

/** How long a shoot-off result stays on screen when the server ends the shoot-off right away. */
const SHOOTOFF_RESULT_HOLD_MS = 10000;

interface FinalCompetitionViewProps {
    header: string;
    laneData: LaneData[] | undefined | null;
    finalCompetitionConfiguration: FinalCompetitionConfiguration | undefined;
}

/**
 * ISSF Elimination Final screen.
 *
 * Layout:
 *   - Left (flex:1): X × Y CSS-grid of FinalCompetitionTarget tiles — full height to navbar
 *   - Right (fixed ~17%): Program name → Phase label → Rank table (if showRankTable)
 *
 * The top header row has been removed so the target grid gains the full available height.
 * Dense rank is computed here from currentTotal (descending).
 * Only lanes with shots receive a rank; tied totals get the same rank.
 * Grid cells are filled in ascending laneNumber order (dense, no gaps) so that e.g.
 * lanes 3–12, 1–8, or 1–5 + 8–11 always fill consecutive grid positions left-to-right / top-to-bottom.
 */
function FinalCompetitionView(props: FinalCompetitionViewProps) {
    const { t } = useTranslation();
    const config = props.finalCompetitionConfiguration;

    // ── Early-computed values needed by hooks (null-safe, before early return) ──────────────
    const laneData = props.laneData ?? [];
    const numberOfCompetitionShots = config?.numberOfCompetitionShots ?? 10;
    const shootOffActive = config?.shootOff?.isActive === true;
    const shootOffTiedLanes = config?.shootOff?.laneNumbers ?? [];

    // Total shots across all lanes — used for "did a new shot arrive?" detection.
    const totalShotCount = laneData.reduce((sum, ld) => sum + (ld.shots?.length ?? 0), 0);

    // ── Client-side shoot-off resolution detection ───────────────────────────
    // The server does not always flip shootOff.isActive to false immediately.
    // We derive `shootOffResolved` from laneData: a tied lane has isEliminated=true (loser flagged
    // after tie-break). Once the next round starts, the server flips shootOff.isActive itself.
    //
    // Do not treat "a tied lane's last shot is a regular shot after shoot-off shots" as resolved: a
    // lane that was in an earlier shoot-off has such shots when it is tied again, which would hide
    // the new shoot-off as soon as its first shot arrives.
    const shootOffResolved: boolean = shootOffActive
        && shootOffTiedLanes.some(ln => laneData.find(ld => ld.laneNumber === ln)?.isEliminated === true);

    // ── Deferred shoot-off resolution ────────────────────────────────────────
    // All visual changes (banner hide, orange shot clear, glow stop) must NOT happen
    // immediately when the tie is broken — they must wait until the next shot arrives.
    // `deferredShootOffResolved` mirrors `shootOffResolved` but only flips true once
    // `totalShotCount` grows past its value at the moment resolution was first detected.
    const [deferredShootOffResolved, setDeferredShootOffResolved] = React.useState(false);
    const resolutionShotCountRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        if (!shootOffResolved) {
            // Resolution no longer active (server deactivated or state changed) — reset.
            resolutionShotCountRef.current = null;
            setDeferredShootOffResolved(false);
            return;
        }
        // First frame resolution is detected: record the current shot count as baseline.
        if (resolutionShotCountRef.current === null) {
            resolutionShotCountRef.current = totalShotCount;
        }
        // A new shot has arrived after the baseline was recorded → apply visual change now.
        if (totalShotCount > resolutionShotCountRef.current) {
            setDeferredShootOffResolved(true);
        }
    }, [shootOffResolved, totalShotCount]);

    // ── Shoot-off ended by the server in the same push that broke the tie ────
    // The server keeps a resolved shoot-off active until the next round starts, so the result can
    // be shown. When no further round follows (medal match) it ends the shoot-off right away; the
    // result is then shown here for SHOOTOFF_RESULT_HOLD_MS anyway.
    const shootOffRound = config?.shootOff?.currentShootOffRound ?? 1;
    const shootOffTiedLanesKey = shootOffTiedLanes.slice().sort((a, b) => a - b).join(',');
    const lastRunningShootOffRef = React.useRef<{ laneNumbers: number[]; round: number } | null>(null);
    const [directlyFinishedShootOff, setDirectlyFinishedShootOff] =
        React.useState<{ laneNumbers: number[]; round: number } | null>(null);

    React.useEffect(() => {
        if (shootOffActive) {
            lastRunningShootOffRef.current = shootOffResolved
                ? null
                : { laneNumbers: [...shootOffTiedLanes], round: shootOffRound };
            setDirectlyFinishedShootOff(null);
            return;
        }
        const lastRunning = lastRunningShootOffRef.current;
        lastRunningShootOffRef.current = null;
        if (lastRunning && lastRunning.laneNumbers.some(ln => laneData.find(ld => ld.laneNumber === ln)?.isEliminated === true)) {
            setDirectlyFinishedShootOff(lastRunning);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shootOffActive, shootOffResolved, shootOffTiedLanesKey, shootOffRound]);

    React.useEffect(() => {
        if (!directlyFinishedShootOff) return;
        const timer = setTimeout(() => setDirectlyFinishedShootOff(null), SHOOTOFF_RESULT_HOLD_MS);
        return () => clearTimeout(timer);
    }, [directlyFinishedShootOff]);

    // What the banner and the spotlight popup show of the shoot-off.
    const shootOffView: ShootOffView | null = shootOffActive && !deferredShootOffResolved
        ? { status: shootOffResolved ? 'finished' : 'running', laneNumbers: shootOffTiedLanes, round: shootOffRound }
        : directlyFinishedShootOff
            ? { status: 'finished', ...directlyFinishedShootOff }
            : null;

    // ── ALL hooks must come before the early return (React rules) ───────────
    if (!config) {
        return (
            <div>
                <h1>{props.header}</h1>
            </div>
        );
    }

    // Capture as a non-nullable const so TypeScript narrows the type inside nested functions.
    const cfg: FinalCompetitionConfiguration = config;

    const xCount = cfg.xLaneCount ?? 4;
    const yCount = cfg.yLaneCount ?? 2;
    const showRankTable = cfg.showRankTable ?? false;

    // Show the weather lower third only during the Preparation (Probeschüsse) phase.
    const showLowerThird = cfg.currentFinalPhase === FinalCompetitionPhase.Value1;

    // Map phase enum value to translation key
    // FinalCompetitionPhase: Value0=Registration, Value1=Preparation, Value2=CompetitionShots, Value3=Elimination, Value4=Finished
    const phaseTranslationKey = (() => {
        switch (cfg.currentFinalPhase) {
            case FinalCompetitionPhase.Value0:  return Translation.PHASE_REGISTRATION;
            case FinalCompetitionPhase.Value1:  return Translation.PHASE_PREPARATION;
            case FinalCompetitionPhase.Value2:  return Translation.PHASE_COMPETITION_SHOTS;
            case FinalCompetitionPhase.Value3:  return Translation.PHASE_ELIMINATION;
            case FinalCompetitionPhase.Value4:  return Translation.PHASE_FINISHED;
            default:                            return Translation.PHASE_REGISTRATION;
        }
    })();

    // Sort lanes by laneNumber ascending and skip completely empty lanes (no participant, no shots).
    // This way the grid is always filled densely with only active/registered lanes,
    // regardless of which lane numbers are used (e.g. 3–12, 1–8, 1–5+8–11).
    const sortedLanes = [...laneData]
        .filter(ld => !!(ld.participantName) || (ld.shots?.length ?? 0) > 0)
        .sort((a, b) => (a.laneNumber ?? 0) - (b.laneNumber ?? 0));

    // ── Display total: use the server's currentTotal which is now authoritative and already
    // excludes shoot-off shot values (backend fix in WinFireLiveService.GetLaneDatas).
    // We keep a thin wrapper for null-safety and fallback consistency across callers.
    const computeDisplayTotal = (ld: { currentTotal?: number | null; shots?: { value?: number | null; isShootOffShot?: boolean }[] | null }): number | null => {
        if (ld.currentTotal != null) {
            return ld.currentTotal;
        }
        // Fallback: sum non-shoot-off shots we have (used before first shot arrives)
        const validShots = (ld.shots ?? []).filter(s => s.isShootOffShot !== true && s.value != null);
        return validShots.length > 0 ? validShots.reduce((sum, s) => sum + (s.value ?? 0), 0) : null;
    };

    // ── Dense rank computation (client-side) ─────────────────────────────────
    // Only lanes with at least one shot are ranked.
    // Uses computeDisplayTotal (server currentTotal minus shoot-off values) for correct ordering.
    // Ties → same rank.
    const sorted = [...laneData]
        .filter(ld => ld.currentTotal != null || (ld.shots?.some(s => s.isShootOffShot !== true) ?? false))
        .sort((a, b) => (computeDisplayTotal(b) ?? 0) - (computeDisplayTotal(a) ?? 0));
    let currentRank = 0;
    let prevTotal: number | null | undefined = undefined;
    const rankMap = new Map<number, number>(); // laneNumber → rank

    for (const ld of sorted) {
        const tot = computeDisplayTotal(ld);
        // Round to 1 decimal before comparing to avoid IEEE-754 floating-point drift.
        const totRounded = tot !== null ? Math.round(tot * 10) : null;
        const prevRounded = prevTotal !== null && prevTotal !== undefined ? Math.round(prevTotal * 10) : prevTotal;
        if (totRounded !== prevRounded) {
            currentRank++;
            prevTotal = tot;
        }
        if (ld.laneNumber !== undefined) {
            rankMap.set(ld.laneNumber, currentRank);
        }
    }

    // Tenth-scoring detection: check shots AND currentTotal (currentTotal may have decimals even
    // if early shots are no longer in shots[], which can happen when shots[] is a partial window).
    const isTenthScoring = laneData.some(ld =>
        (ld.shots?.some(s => s.value != null && !Number.isInteger(s.value)) ?? false) ||
        (ld.currentTotal != null && !Number.isInteger(ld.currentTotal))
    );
    const formatTotal = (v: number | null | undefined): string => {
        if (v == null) return '';
        return isTenthScoring ? v.toFixed(1) : (Number.isInteger(v) ? String(v) : v.toFixed(1));
    };

    // Effective shoot-off active state: server says active AND client hasn't detected
    // resolution YET (deferred — visual change happens only after the next shot arrives).
    const effectiveShootOffActive = shootOffActive && !deferredShootOffResolved;

    // ── Global elimination round: highest round reached by non-eliminated active lanes.
    // Used by FinalCompetitionTarget to clear shots of already-eliminated shooters
    // once the remaining shooters have moved on to a new round.
    const globalEliminationRound = sortedLanes
        .filter(ld => !ld.isEliminated)
        .reduce((max, ld) => {
            const elimShots = (ld.shots ?? []).filter(
                s => (s.shotNr ?? 0) > numberOfCompetitionShots && s.isShootOffShot !== true
            );
            if (elimShots.length === 0) return max;
            return Math.max(max, Math.ceil(elimShots.length / 2));
        }, 0);

    // Grid: fill tiles in ascending laneNumber order (dense — no gap for skipped lane numbers).
    // Extra tiles beyond the number of available lanes are rendered as empty.
    function renderGrid() {
        return Array.from({ length: xCount * yCount }, (_, tileIndex) => {
            const ld = sortedLanes[tileIndex]; // undefined when fewer lanes than tiles
            const rank = ld?.laneNumber !== undefined
                ? (rankMap.get(ld.laneNumber) ?? 0)
                : 0;
            const isInShootOff = effectiveShootOffActive
                && ld?.laneNumber !== undefined
                && (cfg.shootOff?.laneNumbers?.includes(ld.laneNumber) ?? false);
            return (
                <FinalCompetitionTarget
                    key={tileIndex}
                    laneData={ld}
                    numberOfCompetitionShots={numberOfCompetitionShots}
                    displayTargetType={cfg.displayTargetType}
                    currentFinalPhase={cfg.currentFinalPhase}
                    rank={rank}
                    isInShootOff={isInShootOff}
                    globalEliminationRound={globalEliminationRound}
                />
            );
        });
    }

    // ── Phase-complete detection: show pause icon when all expected shots have been fired ──
    // Rules:
    //   Competition (Value2): every active lane has fired all numberOfCompetitionShots.
    //   Elimination (Value3): every non-eliminated active lane has an even count of regular
    //     elimination shots (= current pair is complete) AND no shoot-off is currently active.
    const isPhaseComplete = (() => {
        const activeLanes = sortedLanes;
        if (activeLanes.length === 0) return false;
        switch (cfg.currentFinalPhase) {
            case FinalCompetitionPhase.Value2:
                return activeLanes.every(ld =>
                    (ld.shots?.filter(s => (s.shotNr ?? 0) <= numberOfCompetitionShots).length ?? 0)
                    >= numberOfCompetitionShots
                );
            case FinalCompetitionPhase.Value3: {
                if (effectiveShootOffActive) return false; // shoot-off in progress
                const nonEliminated = activeLanes.filter(ld => !ld.isEliminated);
                if (nonEliminated.length === 0) return false;
                return nonEliminated.every(ld => {
                    const elimShots = (ld.shots ?? []).filter(
                        s => (s.shotNr ?? 0) > numberOfCompetitionShots && s.isShootOffShot !== true
                    );
                    return elimShots.length > 0 && elimShots.length % 2 === 0;
                });
            }
            default:
                return false;
        }
    })();

    // Rank table rows — sorted by dense rank ascending; eliminated athletes pinned to the bottom.
    // Filter out lanes that have neither a participant name nor any shots (empty/unused lanes)
    // to avoid showing "0.0" entries for unregistered slots.
    const rankTableRows = [...laneData]
        .filter(ld => !!(ld.participantName) || (ld.shots?.length ?? 0) > 0)
        .sort((a, b) => {
        const elimDiff = (a.isEliminated ? 1 : 0) - (b.isEliminated ? 1 : 0);
        if (elimDiff !== 0) return elimDiff;
        const ra = a.laneNumber !== undefined ? (rankMap.get(a.laneNumber) ?? 9999) : 9999;
        const rb = b.laneNumber !== undefined ? (rankMap.get(b.laneNumber) ?? 9999) : 9999;
        return ra - rb;
    });

    return (
        // Outer flex — fills height given by MainStage (100vh minus TopLive navbar)
        <div className="fc-view">

            {/* ── Shoot-off banner (fixed overlay, slides in from top) ── */}
            <ShootOffBanner view={shootOffView} laneData={laneData} />

            {/* ── Shoot-off spotlight (centered popup with the enlarged tied targets) ── */}
            <ShootOffSpotlight
                view={shootOffView}
                laneData={laneData}
                renderTile={ld => (
                    <FinalCompetitionTarget
                        laneData={ld}
                        numberOfCompetitionShots={numberOfCompetitionShots}
                        displayTargetType={cfg.displayTargetType}
                        currentFinalPhase={cfg.currentFinalPhase}
                        rank={ld.laneNumber !== undefined ? (rankMap.get(ld.laneNumber) ?? 0) : 0}
                        isInShootOff={true}
                        globalEliminationRound={globalEliminationRound}
                    />
                )}
            />

            {/* ── Left: target grid ── */}
            <div
                className="fc-grid"
                style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${xCount}, 1fr)`,
                    gridTemplateRows: `repeat(${yCount}, 1fr)`,
                }}
            >
                {renderGrid()}
            </div>

            {/* ── Right panel: Program name → Phase → Rank table ── */}
            <div className="fc-sidebar">
                {/* Program name */}
                <div className="fc-program-name" title={props.header}>
                    {props.header}
                </div>

                {/* Phase label */}
                <div className="fc-phase-label">
                    <span>{t(phaseTranslationKey)}</span>
                    <i
                        className="bi bi-pause-circle-fill fc-phase-pause-icon"
                        aria-label="Phase complete — waiting for next shot"
                        style={{ visibility: isPhaseComplete ? 'visible' : 'hidden' }}
                    />
                </div>

                {/* Rank table — fills remaining height with scroll */}
                {showRankTable && (
                    <div className="fc-rank-scroll">
                        <table className="fc-rank-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '16%' }}>#</th>
                                    <th style={{ width: '28%', textAlign: 'right' }}>{t(Translation.CURRENT_TOTAL_SHORT)}</th>
                                    <th style={{ width: '56%' }}>{t(Translation.SHOOTER)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rankTableRows.map((ld, i) => {
                                    const rowRank = ld.laneNumber !== undefined ? (rankMap.get(ld.laneNumber) ?? 0) : 0;
                                    const rankClass = rowRank === 1 ? 'fc-rank-1' : rowRank === 2 ? 'fc-rank-2' : rowRank === 3 ? 'fc-rank-3' : '';
                                    const isInShootOff = effectiveShootOffActive
                                        && ld.laneNumber !== undefined
                                        && (cfg.shootOff?.laneNumbers?.includes(ld.laneNumber) ?? false);
                                    return (
                                        <tr
                                            key={ld.laneNumber ?? i}
                                            className={`${rankClass}${ld.isEliminated ? ' fc-eliminated' : ''}${isInShootOff ? ' fc-shootoff-row' : ''}`}
                                        >
                                            <td className="fc-rank-cell">
                                                {rowRank > 0 ? rowRank : ''}
                                            </td>
                                            <td className="fc-total-cell">
                                                {formatTotal(computeDisplayTotal(ld))}
                                            </td>
                                            <td
                                                className="fc-name-cell"
                                                title={ld.participantName ?? undefined}
                                            >
                                                {ld.participantName ?? ''}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ── Weather lower third — slides in during Preparation (Probeschüsse) phase ── */}
                <WeatherLowerThird
                    locationName={cfg.locationName}
                    weatherData={cfg.weather}
                    visible={showLowerThird}
                />
            </div>
        </div>
    );
}

export default FinalCompetitionView;

